import React from "react";
import { useQuery, useMutation } from "@apollo/client";
import { QUERY_SYSTEM_SETTINGS } from "../../../utils/graphql/systemSettings/queries";
import { UPDATE_SYSTEM_SETTING, INITIALIZE_SYSTEM_SETTINGS } from "../../../utils/graphql/systemSettings/mutations";
import "./SystemSettings.css";

const MATCH_ENGINE_WEIGHT_KEYS = [
  'matchEngineConstraintWeight',
  'matchEngineMeetingsHostedWeight',
  'matchEngineRecentSubsWeight',
  'matchEngineTotalSubsWeight',
  'matchEngineUnassignedApplicationsWeight',
  'matchEngineApplicationTimingWeight',
];

const MATCH_ENGINE_TIMING_KEYS = [
  'matchEngineTimingRelativeWeight',
  'matchEngineTimingHalfLifeHours',
];

const FAIR_TIMING_WINDOW_KEYS = [
  'fairTimingWindowStart',
  'fairTimingWindowEnd',
];

const INTEGRATION_SETTING_KEYS = [
  'googleWorkspaceIntegrationEnabled',
];

const REQUIRED_SYSTEM_SETTING_KEYS = [
  ...MATCH_ENGINE_WEIGHT_KEYS,
  ...MATCH_ENGINE_TIMING_KEYS,
  ...FAIR_TIMING_WINDOW_KEYS,
  ...INTEGRATION_SETTING_KEYS,
];

const MATCH_ENGINE_WEIGHT_PRESETS = [
  {
    id: 'current-balanced',
    label: 'Current Balanced (40 / 30 / 20 / 0 / 0 / 10)',
    values: {
      matchEngineConstraintWeight: 0.40,
      matchEngineMeetingsHostedWeight: 0.30,
      matchEngineRecentSubsWeight: 0.20,
      matchEngineTotalSubsWeight: 0.00,
      matchEngineUnassignedApplicationsWeight: 0.00,
      matchEngineApplicationTimingWeight: 0.10,
    },
  },
  {
    id: 'fairness-balanced',
    label: 'Fairness Balanced (40 / 35 / 15 / 5 / 5 / 0)',
    values: {
      matchEngineConstraintWeight: 0.40,
      matchEngineMeetingsHostedWeight: 0.35,
      matchEngineRecentSubsWeight: 0.15,
      matchEngineTotalSubsWeight: 0.05,
      matchEngineUnassignedApplicationsWeight: 0.05,
      matchEngineApplicationTimingWeight: 0.00,
    },
  },
  {
    id: 'fairness-strong',
    label: 'Fairness Strong (35 / 30 / 10 / 10 / 10 / 5)',
    values: {
      matchEngineConstraintWeight: 0.35,
      matchEngineMeetingsHostedWeight: 0.30,
      matchEngineRecentSubsWeight: 0.10,
      matchEngineTotalSubsWeight: 0.10,
      matchEngineUnassignedApplicationsWeight: 0.10,
      matchEngineApplicationTimingWeight: 0.05,
    },
  },
];

export function SystemSettings() {
  const { data, loading, error, refetch } = useQuery(QUERY_SYSTEM_SETTINGS);
  const [updateSetting] = useMutation(UPDATE_SYSTEM_SETTING, {
    onCompleted: () => refetch(),
  });
  const [initializeSettings] = useMutation(INITIALIZE_SYSTEM_SETTINGS, {
    onCompleted: () => refetch(),
  });

  const [editingSettings, setEditingSettings] = React.useState({});
  const [isBootstrappingMissingSettings, setIsBootstrappingMissingSettings] = React.useState(false);

  const settings = React.useMemo(() => data?.systemSettings || [], [data]);
  const visibleSettings = React.useMemo(
    () => settings.filter((setting) => setting.key !== 'matchEngineRunLock'),
    [settings]
  );

  const scoringWeightSettings = React.useMemo(() => {
    const values = visibleSettings
      .filter((setting) => MATCH_ENGINE_WEIGHT_KEYS.includes(setting.key))
      .reduce((acc, setting) => ({ ...acc, [setting.key]: Number(setting.value) || 0 }), {});

    const total = MATCH_ENGINE_WEIGHT_KEYS.reduce((sum, key) => sum + (values[key] || 0), 0);

    return {
      ...values,
      total,
    };
  }, [visibleSettings]);

  const missingMatchSettingKeys = React.useMemo(() => {
    const available = new Set(visibleSettings.map((setting) => setting.key));
    return REQUIRED_SYSTEM_SETTING_KEYS.filter((key) => !available.has(key));
  }, [visibleSettings]);

  React.useEffect(() => {
    const shouldInitialize = !loading && !error && (settings.length === 0 || missingMatchSettingKeys.length > 0);

    if (!shouldInitialize || isBootstrappingMissingSettings) {
      return;
    }

    setIsBootstrappingMissingSettings(true);
    initializeSettings()
      .catch((err) => {
        console.error("Error initializing missing system settings:", err);
      })
      .finally(() => {
        setIsBootstrappingMissingSettings(false);
      });
  }, [
    loading,
    error,
    settings.length,
    missingMatchSettingKeys.length,
    initializeSettings,
    isBootstrappingMissingSettings,
  ]);

  const handleUpdateSetting = React.useCallback(async (key, value) => {
    try {
      await updateSetting({
        variables: { key, value }
      });
      setEditingSettings(prev => ({ ...prev, [key]: false }));
    } catch (err) {
      console.error("Error updating setting:", err);
      alert(`Error updating setting: ${err.message}`);
    }
  }, [updateSetting]);

  const handleEdit = React.useCallback((key) => {
    setEditingSettings(prev => ({ ...prev, [key]: true }));
  }, []);

  const handleCancel = React.useCallback((key) => {
    setEditingSettings(prev => ({ ...prev, [key]: false }));
  }, []);

  const handleSaveScoringWeights = React.useCallback(async (nextWeights) => {
    try {
      for (const key of MATCH_ENGINE_WEIGHT_KEYS) {
        const value = Number(nextWeights[key]);
        await updateSetting({
          variables: {
            key,
            value: Number.isFinite(value) ? value : 0,
          },
        });
      }

      await refetch();
    } catch (err) {
      console.error("Error updating scoring weights:", err);
      alert(`Error updating scoring weights: ${err.message}`);
    }
  }, [refetch, updateSetting]);

  const handleSaveTimingSettings = React.useCallback(async (nextTimingSettings) => {
    try {
      for (const key of MATCH_ENGINE_TIMING_KEYS) {
        const value = Number(nextTimingSettings[key]);
        await updateSetting({
          variables: {
            key,
            value: Number.isFinite(value) ? value : 0,
          },
        });
      }

      await refetch();
    } catch (err) {
      console.error("Error updating timing settings:", err);
      alert(`Error updating timing settings: ${err.message}`);
    }
  }, [refetch, updateSetting]);

  if (loading) return <div>Loading system settings...</div>;
  if (error) return <div>Error loading system settings: {error.message}</div>;

  // Group settings by category
  const settingsByCategory = visibleSettings.reduce((acc, setting) => {
    const category = setting.category || 'general';
    if (!acc[category]) acc[category] = [];
    acc[category].push(setting);
    return acc;
  }, {});

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-0 fs-4">System Settings</h2>
          <small className="text-muted">Configure system-wide defaults and behavior.</small>
        </div>
      </div>

      <div className="alert alert-info py-2 px-3 mb-3 small settings-info-banner">
        <strong>Match Engine Factor Dials:</strong> Scoring and timing values are runtime dials and do not require a redeploy. Weights are automatically normalized to total 100% at runtime.
        <div className="mt-1">
          Current dial total: <strong>{scoringWeightSettings.total.toFixed(3)}</strong>
        </div>
      </div>

      {Object.entries(settingsByCategory).map(([category, categorySettings]) => (
        <div key={category} className="card mb-4">
          <div className="card-header py-2">
            <h5 className="mb-0 text-capitalize fs-6">{category} Settings</h5>
          </div>
          <div className="card-body py-2">
            {category === 'matching' && categorySettings.some((setting) => MATCH_ENGINE_WEIGHT_KEYS.includes(setting.key)) && (
              <div className="border rounded p-2 mb-2 settings-panel-block">
                <h6 className="mb-1 fs-6">Match Engine Scoring Weights</h6>
                <small className="text-muted d-block mb-1">
                  Tune the six scoring factors here. The engine normalizes these dial values automatically.
                </small>
                <MatchEngineWeightControls
                  settings={categorySettings.filter((setting) => MATCH_ENGINE_WEIGHT_KEYS.includes(setting.key))}
                  onSave={handleSaveScoringWeights}
                />
              </div>
            )}

            {category === 'matching' && categorySettings.some((setting) => MATCH_ENGINE_TIMING_KEYS.includes(setting.key)) && (
              <div className="border rounded p-2 mb-2 settings-panel-block">
                <h6 className="mb-1 fs-6">Application Timing Dials</h6>
                <small className="text-muted d-block mb-1">
                  Relative Blend controls how much rank order vs hour-based decay contributes to timing score.
                </small>
                <MatchEngineTimingControls
                  settings={categorySettings.filter((setting) => MATCH_ENGINE_TIMING_KEYS.includes(setting.key))}
                  onSave={handleSaveTimingSettings}
                />
              </div>
            )}

            {category === 'matching' && !categorySettings.some((setting) => MATCH_ENGINE_WEIGHT_KEYS.includes(setting.key) || MATCH_ENGINE_TIMING_KEYS.includes(setting.key)) && (
              <div className="alert alert-warning py-2 px-3 mb-2 small">
                Match Engine scoring controls are not initialized yet.
                <button
                  type="button"
                  className="btn btn-sm btn-outline-warning ms-2"
                  onClick={() => initializeSettings()}
                  disabled={isBootstrappingMissingSettings}
                >
                  {isBootstrappingMissingSettings ? 'Initializing...' : 'Initialize Controls'}
                </button>
              </div>
            )}

            {categorySettings
              .filter((setting) => !(category === 'matching' && (MATCH_ENGINE_WEIGHT_KEYS.includes(setting.key) || MATCH_ENGINE_TIMING_KEYS.includes(setting.key))))
              .map((setting) => (
                <SettingRow
                  key={setting.key}
                  setting={setting}
                  isEditing={editingSettings[setting.key]}
                  onEdit={() => handleEdit(setting.key)}
                  onUpdate={handleUpdateSetting}
                  onCancel={() => handleCancel(setting.key)}
                />
              ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function MatchEngineWeightControls({ settings, onSave }) {
  const [weightsPercent, setWeightsPercent] = React.useState({});
  const [isSaving, setIsSaving] = React.useState(false);
  const [selectedPreset, setSelectedPreset] = React.useState('');

  React.useEffect(() => {
    const next = {};
    settings.forEach((setting) => {
      next[setting.key] = Number(setting.value || 0) * 100;
    });
    setWeightsPercent(next);
  }, [settings]);

  const total = React.useMemo(() => {
    return MATCH_ENGINE_WEIGHT_KEYS.reduce((sum, key) => sum + (Number(weightsPercent[key]) || 0), 0);
  }, [weightsPercent]);

  const normalizedPercent = React.useCallback((key) => {
    const current = Number(weightsPercent[key]) || 0;
    if (!total) return 0;
    return (current / total) * 100;
  }, [weightsPercent, total]);

  const handleChange = React.useCallback((key, value) => {
    const nextValue = Math.max(0, Number(value) || 0);
    setWeightsPercent((prev) => ({
      ...prev,
      [key]: nextValue,
    }));
  }, []);

  const handleReset = React.useCallback(() => {
    const next = {};
    settings.forEach((setting) => {
      next[setting.key] = Number(setting.value || 0) * 100;
    });
    setWeightsPercent(next);
  }, [settings]);

  const handleSave = React.useCallback(async () => {
    setIsSaving(true);
    try {
      const nextRawWeights = MATCH_ENGINE_WEIGHT_KEYS.reduce((acc, key) => {
        acc[key] = (Number(weightsPercent[key]) || 0) / 100;
        return acc;
      }, {});

      await onSave(nextRawWeights);
    } finally {
      setIsSaving(false);
    }
  }, [onSave, weightsPercent]);

  const handleApplyPreset = React.useCallback(() => {
    const preset = MATCH_ENGINE_WEIGHT_PRESETS.find((p) => p.id === selectedPreset);
    if (!preset) return;

    const next = {};
    MATCH_ENGINE_WEIGHT_KEYS.forEach((key) => {
      next[key] = (Number(preset.values[key]) || 0) * 100;
    });
    setWeightsPercent(next);
  }, [selectedPreset]);

  return (
    <div>
      <div className="d-flex align-items-center gap-2 mb-2">
        <label className="form-label mb-0 small text-muted">Preset</label>
        <select
          className="form-select form-select-sm"
          style={{ maxWidth: '360px' }}
          value={selectedPreset}
          onChange={(e) => setSelectedPreset(e.target.value)}
          disabled={isSaving}
        >
          <option value="">Select a scoring preset…</option>
          {MATCH_ENGINE_WEIGHT_PRESETS.map((preset) => (
            <option key={preset.id} value={preset.id}>{preset.label}</option>
          ))}
        </select>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={handleApplyPreset}
          disabled={isSaving || !selectedPreset}
        >
          Apply Preset
        </button>
      </div>

      {settings.map((setting) => (
        <div key={setting.key} className="row align-items-center py-1 border-bottom">
          <div className="col-md-4">
            <strong className="small">{setting.label}</strong>
            {setting.description && (
              <div className="text-muted" style={{ fontSize: '0.78rem' }}>{setting.description}</div>
            )}
          </div>
          <div className="col-md">
            <input
              type="range"
              className="form-range"
              min="0"
              max="100"
              step="1"
              value={Number(weightsPercent[setting.key]) || 0}
              onChange={(e) => handleChange(setting.key, e.target.value)}
              disabled={isSaving}
            />
          </div>
          <div className="col-md-auto d-flex align-items-center justify-content-md-end">
            <span className="badge settings-value-badge" style={{ fontSize: '0.72rem' }} title="Normalized runtime share">
              {normalizedPercent(setting.key).toFixed(1)}%
            </span>
          </div>
        </div>
      ))}

      <div className="d-flex justify-content-between align-items-center pt-2">
        <small className="text-muted" style={{ fontSize: '0.78rem' }}>
          Raw slider total: <strong>{total.toFixed(0)}%</strong>. Runtime normalized total: <strong>100%</strong>.
        </small>
        <div className="d-flex align-items-center gap-2">
          <button type="button" className="btn btn-outline-secondary" onClick={handleReset} disabled={isSaving}>
            Reset
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Weights'}
          </button>
        </div>
      </div>
    </div>
  );
}

function MatchEngineTimingControls({ settings, onSave }) {
  const [values, setValues] = React.useState({
    matchEngineTimingRelativeWeight: 0.7,
    matchEngineTimingHalfLifeHours: 6,
  });
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    const next = {
      matchEngineTimingRelativeWeight: 0.7,
      matchEngineTimingHalfLifeHours: 6,
    };

    settings.forEach((setting) => {
      const parsed = Number(setting.value);
      if (Number.isFinite(parsed)) {
        next[setting.key] = parsed;
      }
    });

    setValues(next);
  }, [settings]);

  const handleChange = React.useCallback((key, nextValue) => {
    const parsed = Number(nextValue);
    setValues((prev) => ({
      ...prev,
      [key]: Number.isFinite(parsed) ? parsed : 0,
    }));
  }, []);

  const handleReset = React.useCallback(() => {
    const next = {
      matchEngineTimingRelativeWeight: 0.7,
      matchEngineTimingHalfLifeHours: 6,
    };

    settings.forEach((setting) => {
      const parsed = Number(setting.value);
      if (Number.isFinite(parsed)) {
        next[setting.key] = parsed;
      }
    });

    setValues(next);
  }, [settings]);

  const handleSave = React.useCallback(async () => {
    setIsSaving(true);
    try {
      await onSave({
        matchEngineTimingRelativeWeight: Math.min(1, Math.max(0, Number(values.matchEngineTimingRelativeWeight) || 0)),
        matchEngineTimingHalfLifeHours: Math.min(168, Math.max(0.25, Number(values.matchEngineTimingHalfLifeHours) || 0.25)),
      });
    } finally {
      setIsSaving(false);
    }
  }, [onSave, values]);

  return (
    <div>
      <div className="row align-items-center py-1 border-bottom">
        <div className="col-md-4">
          <strong className="small">Relative Blend Weight</strong>
          <div className="text-muted" style={{ fontSize: '0.78rem' }}>
            0 = all hour-decay, 1 = all rank-order timing.
          </div>
        </div>
        <div className="col-md-5">
          <input
            type="range"
            className="form-range"
            min="0"
            max="100"
            step="1"
            value={Math.round((Number(values.matchEngineTimingRelativeWeight) || 0) * 100)}
            onChange={(e) => handleChange('matchEngineTimingRelativeWeight', Number(e.target.value) / 100)}
            disabled={isSaving}
          />
        </div>
        <div className="col-md-3">
          <div className="input-group input-group-sm" style={{ maxWidth: '120px' }}>
            <input
              type="number"
              className="form-control form-control-sm"
              min="0"
              max="1"
              step="0.01"
              value={(Number(values.matchEngineTimingRelativeWeight) || 0).toFixed(2)}
              onChange={(e) => handleChange('matchEngineTimingRelativeWeight', e.target.value)}
              disabled={isSaving}
            />
          </div>
        </div>
      </div>

      <div className="row align-items-center py-1 border-bottom">
        <div className="col-md-4">
          <strong className="small">Half-Life (Hours)</strong>
          <div className="text-muted" style={{ fontSize: '0.78rem' }}>
            Smaller values reward very early applications more strongly.
          </div>
        </div>
        <div className="col-md-5">
          <input
            type="range"
            className="form-range"
            min="1"
            max="48"
            step="1"
            value={Math.round(Number(values.matchEngineTimingHalfLifeHours) || 6)}
            onChange={(e) => handleChange('matchEngineTimingHalfLifeHours', e.target.value)}
            disabled={isSaving}
          />
        </div>
        <div className="col-md-3">
          <div className="input-group input-group-sm" style={{ maxWidth: '120px' }}>
            <input
              type="number"
              className="form-control form-control-sm"
              min="0.25"
              max="168"
              step="0.25"
              value={(Number(values.matchEngineTimingHalfLifeHours) || 0).toFixed(2)}
              onChange={(e) => handleChange('matchEngineTimingHalfLifeHours', e.target.value)}
              disabled={isSaving}
            />
            <span className="input-group-text">hrs</span>
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-end align-items-center pt-2">
        <div className="d-flex align-items-center gap-2">
          <button type="button" className="btn btn-outline-secondary" onClick={handleReset} disabled={isSaving}>
            Reset
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Timing'}
          </button>
        </div>
      </div>
    </div>
  );
}

function SettingRow({ setting, isEditing, onEdit, onUpdate, onCancel }) {
  const formatObjectForEdit = React.useCallback((val) => {
    if (typeof val === 'string') return val;
    try {
      return JSON.stringify(val ?? {}, null, 2);
    } catch {
      return String(val ?? '');
    }
  }, []);

  const [value, setValue] = React.useState(
    setting.type === 'object' ? formatObjectForEdit(setting.value) : setting.value
  );

  React.useEffect(() => {
    setValue(setting.type === 'object' ? formatObjectForEdit(setting.value) : setting.value);
  }, [setting.type, setting.value, formatObjectForEdit]);

  const handleSave = () => {
    if (setting.type === 'object') {
      try {
        const parsedValue = typeof value === 'string' ? JSON.parse(value) : value;
        onUpdate(setting.key, parsedValue);
      } catch (err) {
        alert('Invalid JSON format. Please fix the value before saving.');
      }
      return;
    }

    onUpdate(setting.key, value);
  };

  const renderInput = () => {
    switch (setting.type) {
      case 'number':
        return (
          <input
            type="number"
            className="form-control"
            step="any"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave();
              if (e.key === 'Escape') onCancel();
            }}
          />
        );
      case 'boolean':
        return (
          <select
            className="form-select"
            value={String(value)}
            onChange={(e) => setValue(e.target.value === 'true')}
          >
            <option value="true">True</option>
            <option value="false">False</option>
          </select>
        );
      case 'object':
        return (
          <textarea
            className="form-control"
            rows={6}
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        );
      default:
        return (
          <input
            type="text"
            className="form-control"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave();
              if (e.key === 'Escape') onCancel();
            }}
          />
        );
    }
  };

  const formatValue = (val, type) => {
    switch (type) {
      case 'number':
        return `${val} ${setting.key.includes('Days') ? 'days' : ''}`;
      case 'boolean':
        return val ? 'Enabled' : 'Disabled';
      case 'object':
        try {
          return JSON.stringify(val);
        } catch {
          return String(val);
        }
      default:
        return String(val);
    }
  };

  return (
    <div className="row align-items-center py-2 border-bottom">
      <div className="col-md-4">
        <div>
          <strong className="small">{setting.label}</strong>
          {setting.description && (
            <div className="text-muted" style={{ fontSize: '0.78rem' }}>{setting.description}</div>
          )}
        </div>
      </div>
      <div className="col-md-4">
        {isEditing ? (
          renderInput()
        ) : (
          <div className="d-flex align-items-center">
            <span className="badge settings-value-badge me-2" style={{ fontSize: '0.72rem' }}>
              {formatValue(setting.value, setting.type)}
            </span>
            <small className="text-muted" style={{ fontSize: '0.72rem' }}>({setting.type})</small>
          </div>
        )}
      </div>
      <div className="col-md-4">
        {isEditing ? (
          <div className="d-flex align-items-center gap-2">
            <button className="btn btn-success" onClick={handleSave}>
              <i className="bi bi-check"></i> Save
            </button>
            <button className="btn btn-secondary" onClick={onCancel}>
              <i className="bi bi-x"></i> Cancel
            </button>
          </div>
        ) : (
          <button className="btn btn-outline-primary btn-sm" onClick={onEdit}>
            <i className="bi bi-pencil"></i> Edit
          </button>
        )}
      </div>
    </div>
  );
}